<template>
    <CCard>
        <CCardHeader>
            <h3 class="float-left">Pay Invoices</h3>
            <!-- Button to Go Back -->
            <CButton
                class="float-right hideOnPrint"
                color="secondary"
                @click="$router.go(-1)"
                ><CIcon name="cilChevronLeft" />
                Back
            </CButton>
        </CCardHeader>
        <CCardBody>
            <CDataTable
                hover
                striped
                border
                outlined
                clickable-rows
                @row-clicked="rowClicked"
                :sorter="{ external: false, resetable: true }"
                :items="invoicesList"
                :fields="fields"
                :items-per-page="pageLimit"
                :columnFilter="{ external: false, lazy: false }"
                :active-page="activePage"
                :totals="this.totals"
            >
                <template #under-table>
                    <div>
                        Totals
                        <p class="float-right total">{{ totals | currency }}</p>
                    </div>
                    <hr />
                    <CButton
                        color="success"
                        class="float-right px-4"
                        :disabled="invoicesList && invoicesList.length == 0"
                        @click="payNow"
                        type="submit"
                        ><CIcon name="cil-credit-card" />
                        Pay Now
                    </CButton>
                </template>
                <template #delete="data">
                    <td class="py-2">
                        <CButton
                            size="sm"
                            @click.stop.prevent="deleteThisInvoice(data.item.invoiceNumber)"
                        >
                            <CIcon name="cil-trash" />
                        </CButton>
                    </td>
                </template>
                <template #total="data">
                    <td class="text-right">
                        {{ data.item.total }}
                    </td>
                </template>
                <template #total="data">
                    <td class="text-right">
                        {{ data.item.total | currency }}
                    </td>
                </template>
            </CDataTable>
        </CCardBody>
    </CCard>
</template>

<script>
/**
 * PayInvoices Vue.js pay invoices
 * Display the list of invoices client has selected to pay
 * @module PayInvoices
 * @author Mithun Paulose (Fresche Solutions)
 * @date June 2020
 **/
import { mapState, mapMutations, mapActions, mapGetters } from "vuex";
// Two-way mapping of local state fields to vuex
import { mapFields } from "vuex-map-fields";

export default {
    name: "PayInvoices",
    created: function() {
        this.init();
    },
    data() {
        return {
            selectedInvoices: null,
            invoicesList: null,
            openInvoiceList: null,
            activePage: 1,
            pageLimit: 9999,
            totals: 0,
            fields: [
                {
                    key: "delete",
                    label: "",
                    _style: "width:1%",
                    sorter: false,
                    filter: false
                },
                {
                    key: "invoiceNumber",
                    label: "Invoice Number",
                    _style: "text-align: center"
                },
                {
                    key: "invoiceDate",
                    label: "Invoice Date",
                    _style: "text-align: center"
                },
                {
                    key: "dueDate",
                    label: "Due Date",
                    _style: "text-align: center"
                },
                {
                    key: "total",
                    label: "Invoice Total",
                    _style: "text-align: center"
                }
            ]
        };
    },
    watch: {
        $route: {
            immediate: true,
            handler(route) {
                if (route.query && route.query.page) {
                    this.activePage = Number(route.query.page);
                }
            }
        }
    },
    methods: {
        ...mapActions("Invoice", ["fetchOpenInvoiceList", "fetchInvoiceDetails", "updateSelectedInvoices"]),
        ...mapGetters("Invoice", ["getOpenInvoices","getInvoiceDetails","getSelectedInvoices"]),
        async init() {

            // Update the redirect from route, if provided
            if (this.$route.params.redirect) {
                this.redirectTo = this.$route.params.redirect;
            }

            // Get the selected invoice list
            this.selectedInvoices = this.getSelectedInvoices();
            if (!this.selectedInvoices || this.selectedInvoices.length == 0) {
                return;
            }

            // Fetch and populate open invoices
            let openInvoices = this.getOpenInvoices();
            if (!openInvoices || openInvoices.length == 0) {
                await this.fetchOpenInvoiceList();
                openInvoices = this.getOpenInvoices();
            }

            // Require open invoices
            if (!openInvoices || !openInvoices.invoices || openInvoices.invoices.length == 0) {
                return;
            }
            this.openInvoiceList = openInvoices.invoices;

            // Get just the selected invoices from the invoice list, using numeric invoice numbers.
            this.selectedInvoices = this.selectedInvoices.map(Number);
            this.invoicesList = this.openInvoiceList.filter(
                item => this.selectedInvoices.indexOf( parseInt(item.invoiceNumber) ) !== -1
            );

            this.calculateTotals();
        },

        async getLists() {

            // Get the selected invoice list
            var response = this.getSelectedInvoices();
            if (response) {
                this.selectedInvoices = response;
            }

            // Get the open invoice list
            var openInvoices = this.getOpenInvoices();
            if (openInvoices) {
                this.openInvoiceList = openInvoices.invoices;
            }
        },
        //Filter the full invoices list using the selected invoices array
        filterList() {
            var filtered = this.selectedInvoices;
            this.invoicesList = this.openInvoiceList.filter(function(item) {
                return filtered.indexOf( parseInt(item.invoiceNumber) ) !== -1;
            });
        },
        deleteThisInvoice(invoiceNumber) {

            invoiceNumber = parseInt(invoiceNumber);

            // Delete by index
            let pos = this.selectedInvoices.indexOf(invoiceNumber);
            if (pos !== -1) {
                this.selectedInvoices.splice(pos, 1);
            }
            this.updateSelectedInvoices(this.selectedInvoices);
            this.getLists();
            this.filterList();
            this.calculateTotals();
        },
        calculateTotals() {
            this.totals = 0;
            for (let i = 0; i < this.invoicesList.length; i++) {
                this.totals =
                    parseFloat(this.totals) +
                    parseFloat(this.invoicesList[i].total);
            }
        },
        payNow() {
            this.$router.push({ path: "/invoices/paymentConfirmation" });
        },
        getTotals() {
            return this.totals;
        },

        // Row clicked - View an order on its display page
        rowClicked(row) {
            this.$router.push({ path: `/invoices/display/${row.invoiceNumber}` });
        },
    }
};
</script>
<style scoped>
    .total {
        padding-right: 0.75rem;
    }
</style>
